import React, {useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import {MapTo, EditableComponent} from '@adobe/aem-react-editable-components';
import {openProductModal} from '@/features/product/product-modal/slices/product-modal-slice';
import {AEM_MAPPED_TYPES} from '@/constants';
import {useProductPricing} from '@/features/product/hooks/useProductPricing';
import {PRODUCT_TYPES} from '@/features/product/product-constants';

const LABEL_PREFIX = 'productPricingModal.labels.';
const PRODUCT_PRICING_LOCALE_RELOAD_ATTEMPT_KEY = 'productPricingModal.localeReloadAttempted';

/**
 * Product Pricing Modal component that renders a modal via portal.
 * Auto-wires anchor links with href="#${id}" on mount.
 * @param {Object} props - Component props
 * @param {string} props.id - Modal element ID, sourced from the AEM-authored modalId property
 * @returns {React.ReactPortal} Portal rendering modal to document body
 */
const ProductPricingModalConfig = {
  emptyLabel: 'Product Pricing Modal',
  isEmpty: (props) => !props.id,
};

const ProductPricingModalBase = ({modalId, productType, productPaths, defaultProduct, ...rest}) => {
  const dispatch = useDispatch();
  const countryCode = productType === PRODUCT_TYPES.TRAINING_VOUCHERS ? 'US' : null;
  const selectedZone = productType === PRODUCT_TYPES.TRAINING_VOUCHERS ? 'US' : null;

  // Warm the RTK Query cache on mount so data is ready when the modal opens.
  const {data: productData} = useProductPricing(productPaths, countryCode, selectedZone);

  // HOTFIX: Retry once when product pricing ignores the requested US zone for training vouchers.
  useEffect(() => {
    if (productType !== PRODUCT_TYPES.TRAINING_VOUCHERS || !productData?.locale) return;

    if (productData.locale.zone === 'US') return;

    if (!globalThis.sessionStorage.getItem(PRODUCT_PRICING_LOCALE_RELOAD_ATTEMPT_KEY)) {
      globalThis.sessionStorage.setItem(PRODUCT_PRICING_LOCALE_RELOAD_ATTEMPT_KEY, 'true');
      globalThis.location.reload();
    }
  }, [productData?.locale, productType]);

  const labelsKey = Object.entries(rest)
    .filter(([key]) => key.startsWith(LABEL_PREFIX))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value}`)
    .join(',');

  const labels = useMemo(
    () =>
      Object.entries(rest)
        .filter(([key]) => key.startsWith(LABEL_PREFIX))
        .reduce((acc, [key, value]) => {
          acc[key.slice(LABEL_PREFIX.length)] = value;
          return acc;
        }, {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [labelsKey],
  );

  useEffect(() => {
    const triggers = modalId ? document.querySelectorAll(`[href="#${modalId}"]`) : [];
    const handleClick = (event) => {
      event.preventDefault();
      dispatch(openProductModal({type: productType, labels, productPaths, defaultProductPath: defaultProduct}));
    };

    triggers.forEach((btn) => {
      btn.addEventListener('click', handleClick);
    });

    return () => {
      triggers.forEach((btn) => {
        btn.removeEventListener('click', handleClick);
      });
    };
  }, [dispatch, modalId, productType, labels, productPaths, defaultProduct]);

  return null;
};

ProductPricingModalBase.propTypes = {
  modalId: PropTypes.string,
  productType: PropTypes.string,
  productPaths: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  defaultProduct: PropTypes.string,
  // Additional props matching 'productPricingModal.labels.*' are AEM-authored strings
  // and are grouped into a labels object at runtime via the LABEL_PREFIX convention.
};

const ProductPricingModal = (props) => {
  return (
    <EditableComponent config={ProductPricingModalConfig} {...props}>
      <ProductPricingModalBase {...props} />
    </EditableComponent>
  );
};

MapTo(AEM_MAPPED_TYPES.PRODUCT_PRICING_MODAL)(ProductPricingModal);

export default ProductPricingModal;
